import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import FavouriteGroups from './FavouriteGroups';
import ProductResultTable, { type Product } from './ProductResultTable';
import type { SearchResponseSettings } from './ProductSearchForm';
import MyAccountPageShell from './MyAccountPageShell';
import { PageShellLoading, TableSkeleton, ToolbarSkeleton } from './MyAccountLoadingStates';
import {
    FAVOURITE_EVENT_NAME,
    FavouriteGroupSummary,
    fetchFavouritesData,
    getFavouritesConfig,
    postFavouriteAction,
} from '../utils/favourites';

interface FavouriteProduct extends Product {
    favourite_group?: string;
    favourite_group_slug?: string;
}

interface FavouritesResponseData {
    mode?: 'flat_list' | 'groups_overview' | 'group_detail';
    favourites?: FavouriteProduct[];
    groups?: FavouriteGroupSummary[];
    group?: FavouriteGroupSummary | null;
    settings?: SearchResponseSettings;
    meta?: {
        total?: number;
        filtered?: number;
        total_groups?: number;
        total_favourites?: number;
    };
}

interface GroupMutationResponse {
    message?: string;
    group_summaries?: FavouriteGroupSummary[];
}

const Favourites: React.FC = () => {
    const navigate = useNavigate();
    const { groupSlug } = useParams<{ groupSlug?: string }>();
    const favouritesConfig = getFavouritesConfig();
    const groupsEnabled = favouritesConfig.groups_enabled === true;
    const [favourites, setFavourites] = useState<FavouriteProduct[] | null>(null);
    const [groups, setGroups] = useState<FavouriteGroupSummary[]>([]);
    const [activeGroup, setActiveGroup] = useState<FavouriteGroupSummary | null>(null);
    const [settings, setSettings] = useState<SearchResponseSettings>({});
    const [mode, setMode] = useState<'flat_list' | 'groups_overview' | 'group_detail'>('flat_list');
    const [resolvedGroupsEnabled, setResolvedGroupsEnabled] = useState(groupsEnabled);
    const [loading, setLoading] = useState(true);
    const [mutating, setMutating] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const defaultGroupName = String(favouritesConfig.default_group_name || 'Default').trim() || 'Default';

    const loadFavourites = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetchFavouritesData<FavouritesResponseData>((groupsEnabled || !!groupSlug)
                ? (groupSlug
                    ? { view: 'group_detail', group_slug: groupSlug }
                    : { view: 'groups_overview' })
                : {});

            const responseMode = response.mode || (groupsEnabled ? (groupSlug ? 'group_detail' : 'groups_overview') : 'flat_list');
            setMode(responseMode);
            setResolvedGroupsEnabled(responseMode !== 'flat_list');
            setGroups(Array.isArray(response.groups) ? response.groups : []);
            setActiveGroup(response.group || null);
            setFavourites(Array.isArray(response.favourites) ? response.favourites : []);
            setSettings(response.settings && typeof response.settings === 'object'
                ? response.settings
                : {});
        } catch (err) {
            console.error('Error fetching favourites:', err);
            setError(err instanceof Error ? err.message : 'Failed to load favourites. Please try again.');
            setFavourites([]);
            setGroups([]);
            setActiveGroup(null);
            setResolvedGroupsEnabled(groupsEnabled);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        void loadFavourites();
    }, [groupSlug, groupsEnabled]);

    useEffect(() => {
        setSearchTerm('');
    }, [groupSlug, groupsEnabled]);

    useEffect(() => {
        const handleFavouriteChanged = () => {
            void loadFavourites();
        };

        document.addEventListener(FAVOURITE_EVENT_NAME, handleFavouriteChanged);
        return () => {
            document.removeEventListener(FAVOURITE_EVENT_NAME, handleFavouriteChanged);
        };
    }, [groupSlug, groupsEnabled]);

    const rows = Array.isArray(favourites) ? favourites : [];
    const overviewGroups = useMemo<FavouriteGroupSummary[]>(() => {
        if (!resolvedGroupsEnabled || mode !== 'groups_overview') {
            return groups;
        }

        if (groups.length > 0) {
            return groups;
        }

        return [{
            name: defaultGroupName,
            slug: 'default',
            count: 0,
            is_default: true,
        }];
    }, [defaultGroupName, groups, mode, resolvedGroupsEnabled]);

    const filteredRows = useMemo(() => {
        const search = searchTerm.trim().toLowerCase();
        if (search === '') {
            return rows;
        }

        return rows.filter((product) => {
            return [
                product.name,
                product.sku,
                product.description,
                product.favourite_group,
            ]
                .join(' ')
                .toLowerCase()
                .includes(search);
        });
    }, [rows, searchTerm]);

    const browseProductsUrl = useMemo(() => {
        const configuredUrl = String(settings.browse_products_url || '').trim();
        if (configuredUrl !== '') {
            return configuredUrl;
        }

        return '/shop/';
    }, [settings.browse_products_url]);

    const handleCreateGroup = async (groupName: string) => {
        setMutating(true);
        try {
            const response = await postFavouriteAction<GroupMutationResponse>('create_favourite_group', {
                group_name: groupName,
            });
            toast.success(response.message || 'Group created.');
            await loadFavourites();
        } finally {
            setMutating(false);
        }
    };

    const handleRenameGroup = async (group: FavouriteGroupSummary, groupName: string) => {
        setMutating(true);
        try {
            const response = await postFavouriteAction<GroupMutationResponse>('rename_favourite_group', {
                current_group_name: group.name,
                new_group_name: groupName,
            });
            toast.success(response.message || 'Group renamed.');
            await loadFavourites();
        } finally {
            setMutating(false);
        }
    };

    const handleDeleteGroup = async (group: FavouriteGroupSummary) => {
        if (group.is_default) {
            return;
        }

        const confirmed = window.confirm(`Delete ${group.name}? Items in this group will move to Default.`);
        if (!confirmed) {
            return;
        }

        setMutating(true);
        try {
            const response = await postFavouriteAction<GroupMutationResponse>('delete_favourite_group', {
                group_name: group.name,
            });
            toast.success(response.message || 'Group deleted.');
            await loadFavourites();
        } finally {
            setMutating(false);
        }
    };

    if (loading && favourites === null) {
        return (
            <PageShellLoading
                showMeta={true}
                titleWidth="24%"
                subtitleWidth="48%"
                metaWidth="72px"
            >
                <ToolbarSkeleton columns={['col-12']} />
                <TableSkeleton columns={8} rows={8} />
            </PageShellLoading>
        );
    }

    if (error) {
        return (
            <MyAccountPageShell
                title={resolvedGroupsEnabled ? 'My Favourite Groups' : 'My Favourites'}
                subtitle={resolvedGroupsEnabled
                    ? 'Organise and browse your saved product groups.'
                    : 'Products you have bookmarked for quick access.'}
                actions={resolvedGroupsEnabled && groupSlug ? (
                    <button type="button" className="button weka-primary-btn" onClick={() => navigate('/favourites')}>
                        Back to Groups
                    </button>
                ) : undefined}
            >
                <div className="woocommerce-error" role="alert">{error}</div>
                <button type="button" onClick={() => { void loadFavourites(); }} className="button">
                    Retry
                </button>
            </MyAccountPageShell>
        );
    }

    if (resolvedGroupsEnabled && mode === 'groups_overview') {
        return (
            <MyAccountPageShell
                title="My Favourite Groups"
                subtitle="Browse saved groups and jump into the products inside each one."
                meta={`${overviewGroups.length} ${overviewGroups.length === 1 ? 'group' : 'groups'}`}
            >
                <FavouriteGroups
                    groups={overviewGroups}
                    onSelectGroup={(group) => navigate(`/favourites/${group.slug}`)}
                    onCreateGroup={handleCreateGroup}
                    onRenameGroup={handleRenameGroup}
                    onDeleteGroup={handleDeleteGroup}
                    isSubmitting={mutating}
                />
            </MyAccountPageShell>
        );
    }

    if (rows.length === 0) {
        return (
            <MyAccountPageShell
                title={resolvedGroupsEnabled ? (activeGroup?.name || 'Favourite Group') : 'My Favourites'}
                subtitle={resolvedGroupsEnabled
                    ? 'This group does not have any saved products yet.'
                    : 'Products you have bookmarked for quick access.'}
                actions={resolvedGroupsEnabled ? (
                    <button type="button" className="button weka-primary-btn" onClick={() => navigate('/favourites')}>
                        Back to Groups
                    </button>
                ) : undefined}
            >
                <div className="woocommerce-message woocommerce-message--info woocommerce-Message woocommerce-Message--info woocommerce-info">
                    {resolvedGroupsEnabled ? 'No products are saved in this group yet.' : 'You have no favourite products yet.'}
                </div>
                <div className="d-flex gap-2 flex-wrap">
                    <a href={browseProductsUrl} className="button">
                        Browse products
                    </a>
                    {resolvedGroupsEnabled && (
                        <button type="button" className="button weka-primary-btn" onClick={() => navigate('/favourites')}>
                            Back to Groups
                        </button>
                    )}
                </div>
            </MyAccountPageShell>
        );
    }

    return (
        <MyAccountPageShell
            title={resolvedGroupsEnabled ? (activeGroup?.name || 'Favourite Group') : 'My Favourites'}
            subtitle={resolvedGroupsEnabled
                ? 'Products saved in this favourite group.'
                : 'Products you have bookmarked for quick access.'}
            meta={filteredRows.length === rows.length
                ? `${rows.length} ${rows.length === 1 ? 'item' : 'items'}`
                : `Showing ${filteredRows.length} of ${rows.length}`}
            actions={resolvedGroupsEnabled ? (
                <button type="button" className="button weka-primary-btn" onClick={() => navigate('/favourites')}>
                    Back to Groups
                </button>
            ) : undefined}
        >
            <div className="input-group mb-3">
                <span className="input-group-text">
                    <i className="fas fa-search" aria-hidden="true"></i>
                </span>
                <input
                    type="text"
                    className="form-control"
                    placeholder={resolvedGroupsEnabled ? 'Search this group...' : 'Search favourites...'}
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                />
            </div>

            <ProductResultTable
                searchResponse={{
                    total_pages: 1,
                    current_page: 1,
                    total_results: filteredRows.length,
                    results: filteredRows,
                    settings,
                }}
                onSort={() => undefined}
                sort=""
                templateCsvUrl=""
                onPageChange={() => undefined}
                productDetailBasePath=""
                showToolbar={false}
                embedded={true}
                emptyMessage={resolvedGroupsEnabled ? 'No products in this group match your search.' : 'No favourite products match your search.'}
                fallbackProductLinksOpenInNewTab={false}
            />
        </MyAccountPageShell>
    );
};

export default Favourites;
